import { useState } from "react"
import Manage from "@/v2-backoffice-client/components/manage"
import type { TenantFragment } from "@/v2-backoffice-client/tenant/__types__/tenant.fragment"
import type { UserTenantFragment } from "@/v2-backoffice-client/user/tenant/__types__/user.tenant.fragment"
import { useUserTenantTypeQuery } from "@/v2-backoffice-client/user/tenant/type/user.tenant.type.query"
import { useUserViewQuery } from "@/v2-backoffice-client/user/view/user.view.query"
import UserTenantManageMemberItem from "@/v2-backoffice-client/user/tenant/manage/UserTenantManageMemberItem"
import UserTenantManageAddedItem from "@/v2-backoffice-client/user/tenant/manage/UserTenantManageAddedItem"
import UserTenantManageSearch from "@/v2-backoffice-client/user/tenant/manage/UserTenantManageSearch"
import UserTenantManageFooter from "@/v2-backoffice-client/user/tenant/manage/UserTenantManageFooter"
import { DEFAULT_TENANT_USER_TYPE_CODE } from "@/v2-backoffice-client/tenant/user/tenant.user.constants"

type AddedItem = {
  tenant: TenantFragment
  roleCode: number
}

export type UserTenantManageDialogProps = {
  open: boolean
  onOpenChange(open: boolean): void
  userId: number
  userName?: string
  existingUserTenants?: UserTenantFragment[]
}

function UserTenantManageDialog(props: UserTenantManageDialogProps) {
  const {
    open,
    onOpenChange,
    userId,
    userName,
    existingUserTenants = [],
  } = props

  const [search, setSearch] = useState("")
  const [searchAll, setSearchAll] = useState(true)

  const [addedItems, setAddedItems] = useState<Map<number, AddedItem>>(
    new Map(),
  )
  const [removedIds, setRemovedIds] = useState<Set<number>>(new Set())

  const { data: typeData } = useUserTenantTypeQuery({
    skip: !open,
    fetchPolicy: "cache-first",
  })

  // The user's owner, for the guard on each search hit (a cache hit: the
  // user panel or row this dialog opens from has already read the user).
  const { data: userData } = useUserViewQuery({
    skip: !open,
    variables: { id: userId },
  })
  const user = userData?.getUserById

  const typesByCode = new Map(
    (typeData?.getUserTenantTypes ?? []).map((t) => [t.Code, t.Name]),
  )

  function handleOpenChange(nextOpen: boolean) {
    onOpenChange(nextOpen)
    if (!nextOpen) {
      setAddedItems(new Map())
      setRemovedIds(new Set())
      setSearch("")
      setSearchAll(true)
    }
  }

  function handleAdded(tenant: TenantFragment, roleCode: number) {
    setAddedItems((prev) => {
      const next = new Map(prev)
      next.set(tenant.Id, { tenant, roleCode })
      return next
    })
    setRemovedIds((prev) => {
      if (!prev.has(tenant.Id)) return prev
      const next = new Set(prev)
      next.delete(tenant.Id)
      return next
    })
  }

  function handleRemoved(tenantId: number) {
    setRemovedIds((prev) => new Set(prev).add(tenantId))
    setAddedItems((prev) => {
      if (!prev.has(tenantId)) return prev
      const next = new Map(prev)
      next.delete(tenantId)
      return next
    })
  }

  function handleCreated(tenant: TenantFragment) {
    handleAdded(tenant, DEFAULT_TENANT_USER_TYPE_CODE)
  }

  const existingTenantIds = new Set(
    existingUserTenants.map((ut) => ut.TenantId),
  )

  function isMember(tenantId: number) {
    if (removedIds.has(tenantId)) return false
    if (addedItems.has(tenantId)) return true
    return existingTenantIds.has(tenantId)
  }

  const searchLower = search.toLowerCase()

  function matchesName(name: string, extra: string) {
    if (!searchLower) return true
    return (
      name.toLowerCase().includes(searchLower) ||
      extra.toLowerCase().includes(searchLower)
    )
  }

  const visibleExisting = existingUserTenants.filter((ut) => {
    if (removedIds.has(ut.TenantId)) return false
    if (!searchAll) return true
    const tenant = ut.Tenant
    return matchesName(tenant?.Name ?? "", tenant?.CustomerName ?? "")
  })

  const visibleAdded = [...addedItems.values()].filter((item) => {
    if (existingTenantIds.has(item.tenant.Id)) return false
    if (!searchAll) return true
    return matchesName(item.tenant.Name ?? "", item.tenant.CustomerName ?? "")
  })

  const totalMembers = visibleExisting.length + visibleAdded.length
  const title = userName
    ? `Manage Teams for User: ${userName}`
    : "Manage Teams for User"

  return (
    <Manage.Dialog open={open} onOpenChange={handleOpenChange} title={title}>
      <Manage.Search
        onSearch={setSearch}
        placeholder="Search teams by name..."
        searchAll={searchAll}
        onSearchAllChange={setSearchAll}
      />

      {totalMembers > 0 && (
        <Manage.Section label={`Teams already a member of (${totalMembers})`}>
          <Manage.List>
            {visibleExisting.map((ut) => (
              <UserTenantManageMemberItem
                key={ut.Id}
                userTenant={ut}
                userId={userId}
                onRemoved={() => handleRemoved(ut.TenantId)}
              />
            ))}
            {visibleAdded.map((item) => (
              <UserTenantManageAddedItem
                key={item.tenant.Id}
                tenant={item.tenant}
                userId={userId}
                roleName={typesByCode.get(item.roleCode)}
                onRemoved={() => handleRemoved(item.tenant.Id)}
              />
            ))}
          </Manage.List>
        </Manage.Section>
      )}

      <UserTenantManageSearch
        open={open}
        userId={userId}
        user={user}
        debouncedSearch={search}
        isMember={isMember}
        onAdded={handleAdded}
      />

      <UserTenantManageFooter userId={userId} onCreated={handleCreated} />
    </Manage.Dialog>
  )
}

export default UserTenantManageDialog
